"use client";

import { useQuery, type QueryClient } from "@tanstack/react-query";
import { paginate } from "@theta/api-client";
import { getGroupRulesPage, type GroupRule } from "@/lib/api/groupRules";
import { retryUnlessRefused } from "@/lib/hooks/queryRetry";

/**
 * A group's numbered rules, as ONE FLAT LIST (OSO-648).
 *
 * Every page is walked here, once, like `useMyGroups`: the server caps the
 * list at 15, so this is one request in practice, and the three surfaces that
 * render it — the group sidebar, the settings editor, the moderation queue's
 * rule picker — must not each thread a `hasMore` they will get wrong.
 *
 * THE KEY IS THE REF AS THE CALLER SPELLS IT. A group is addressable by id
 * and by slug and the three callers agree on slug; `invalidateGroupRules`
 * drops the whole prefix anyway, so a mixed-spelling caller costs a refetch,
 * not a stale list.
 */
export function useGroupRules(
  ref: string | number | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ["v1", "group-rules", String(ref)],
    enabled: enabled && ref !== undefined && ref !== "",
    queryFn: async () => {
      const rules: GroupRule[] = [];
      for await (const rule of paginate<GroupRule>((cursor) =>
        getGroupRulesPage(ref!, cursor),
      )) {
        rules.push(rule);
      }
      return rules;
    },
    retry: retryUnlessRefused,
    staleTime: 60 * 1000,
  });
}

/** After any rule mutation — every cached spelling of every group's list. */
export function invalidateGroupRules(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["v1", "group-rules"] });
}
